import { OktaAuth, type OktaAuthOptions } from '@okta/okta-auth-js';

import { logger } from '../utils/logger';

export interface OktaEnvironmentConfig {
  OKTA_ISSUER: string;
  OKTA_CLIENT_ID: string;
}

export interface OktaApplicationConfig {
  APPLICATION_PATH: string;
}

export interface LocationProvider {
  origin: string;
}

export function validateOktaConfig(
  env: Partial<OktaEnvironmentConfig>
): env is OktaEnvironmentConfig {
  if (!env.OKTA_ISSUER || !env.OKTA_CLIENT_ID) {
    throw new Error(
      'Missing required Okta configuration. Please check your .env file and ensure OKTA_ISSUER and OKTA_CLIENT_ID are set'
    );
  }
  return true;
}

export function getOktaConfig(
  env: OktaEnvironmentConfig,
  appConfig: OktaApplicationConfig,
  location: LocationProvider
): OktaAuthOptions {
  validateOktaConfig(env);

  return {
    issuer: env.OKTA_ISSUER,
    clientId: env.OKTA_CLIENT_ID,
    redirectUri: `${location.origin}${appConfig.APPLICATION_PATH}/login/callback`,
    postLogoutRedirectUri: `${location.origin}${appConfig.APPLICATION_PATH}`,
    scopes: ['openid', 'profile', 'email'],
    pkce: true,
    tokenManager: {
      storageKey: 'okta-token-storage',
    },
  };
}

export function createOktaAuth(
  env: OktaEnvironmentConfig,
  appConfig: OktaApplicationConfig,
  location: LocationProvider
): OktaAuth {
  const config = getOktaConfig(env, appConfig, location);
  logger.info('Creating Okta Configuration:', {
    issuer: env.OKTA_ISSUER,
    clientId: env.OKTA_CLIENT_ID ? '***' : undefined,
    origin: location.origin,
  });
  return new OktaAuth(config);
}
